import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { ArrowLeft, CheckCircle, XCircle, AlertCircle, X } from "lucide-react";
import { GetSingleVideoCollectionThunk } from "../../redux/states/videoCollection/thunk";

const ViewVideoCollection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();

  const { singlevideocollection, loading } = useSelector(
    (state) => state.videocollection
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (uuid) {
      dispatch(GetSingleVideoCollectionThunk(uuid));
    }
  }, [dispatch, uuid]);

  /* -------------------- LOADING -------------------- */
  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              Loading Video Collection...
            </p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  /* -------------------- NOT FOUND -------------------- */
  if (!singlevideocollection) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <XCircle className="w-14 h-14 text-red-500 mb-3" />
          <h2 className="text-xl font-semibold">Video Collection Not Found</h2>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </NonAuthLayout>
    );
  }

  const review = singlevideocollection;

  /* -------------------- STATUS BADGE -------------------- */
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return {
          text: "Active",
          color: "bg-green-100 text-green-700",
          icon: CheckCircle,
        };
      case "inactive":
        return {
          text: "Inactive",
          color: "bg-red-100 text-red-700",
          icon: XCircle,
        };
      default:
        return {
          text: "Unknown",
          color: "bg-gray-100 text-gray-700",
          icon: AlertCircle,
        };
    }
  };

  const statusData = getStatusBadge(review.status);
  const StatusIcon = statusData.icon;

  const videoUrl = review.vid_path ? `${API_URL}/${review.vid_path}` : null;

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-4 bg-gray-50">
        <div className="max-w-12xl mx-auto space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between bg-white p-4 rounded-xl border">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
            >
              <ArrowLeft size={18} />
              Back
            </button>

            <h2 className="font-semibold text-gray-800">
              Video Collection Details
            </h2>

            <span
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${statusData.color}`}
            >
              <StatusIcon size={14} />
              {statusData.text}
            </span>
          </div>

          {/* Details Card */}
          <div className="bg-white p-6 rounded-xl border space-y-4">
            <h3 className="font-semibold text-gray-700">
              Collection Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoRow label="UUID" value={review.uuid} />
              <InfoRow label="Title" value={review.title} />
              <InfoRow label="Product ID" value={review.product_id} />
              <InfoRow label="Product Name" value={review.product_id_data} />
              <InfoRow label="Website Link" value={review.link} />
              <InfoRow
                label="Created At"
                value={new Date(review.created_at).toLocaleString("en-IN")}
              />
            </div>

            {/* Video Preview */}
            {videoUrl && (
              <div className="pt-4">
                <h4 className="font-medium text-gray-700 mb-2">
                  Video Preview
                </h4>
                <video
                  src={videoUrl}
                  controls
                  className="max-h-64 rounded-lg border cursor-pointer"
                  onClick={() => setIsModalOpen(true)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="relative bg-white rounded-xl max-w-4xl w-full mx-4 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-red-500"
            >
              <X />
            </button>

            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full max-h-[80vh] rounded-lg"
            />
          </div>
        </div>
      )}
    </NonAuthLayout>
  );
};

/* -------------------- INFO ROW -------------------- */
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-center bg-gray-50 px-4 py-2 rounded-lg border">
    <span className="text-sm font-medium text-gray-600">{label}</span>
    <span className="text-sm text-gray-800">{value || "—"}</span>
  </div>
);

export default ViewVideoCollection;
